<script setup lang="ts">
/**
 * ProgressiveMockup — 渐进式展示 mockup
 * v5: MatchCard.vue 多级折叠（仅杯赛淘汰赛阶段）
 * 足球 | 篮球切换：足球 = 世界杯决赛（回放+集锦默认展开，加时/点球默认折叠）
 *            篮球 = NBA 总决赛（回放+集锦默认展开，加时折叠；篮球无点球）
 * 不显示比分/分钟数/剧透标签；只 vs 居中
 */
import { ref, watch } from 'vue'
import YfbIcon from '@/components/YfbIcon.vue'
import type { IconName } from '@/types/icons'

type Sport = 'football' | 'basketball'

interface VideoChip {
  platform: string
  platformName: string
  title: string
}

interface SectionData {
  key: string
  label: string
  icon: IconName
  ghost: boolean
  chips: VideoChip[]
}

const sport = ref<Sport>('football')

const sportTabs: { key: Sport; label: string; icon: IconName }[] = [
  { key: 'football', label: '足球', icon: 'ph:soccer-ball-bold' },
  { key: 'basketball', label: '篮球', icon: 'ph:basketball-bold' }
]

const config: Record<Sport, { league: string; date: string; home: string; away: string; sections: SectionData[] }> = {
  football: {
    league: '世界杯决赛',
    date: '12-18 23:00',
    home: '阿根廷',
    away: '法国',
    sections: [
      {
        key: 'replay',
        label: '全场回放',
        icon: 'ph:play-circle-bold',
        ghost: false,
        chips: [
          { platform: 'cctv', platformName: '央视', title: '上半场' },
          { platform: 'cctv', platformName: '央视', title: '下半场' },
          { platform: 'cctv', platformName: '央视', title: '加时' }
        ]
      },
      {
        key: 'highlight',
        label: '比赛集锦',
        icon: 'ph:lightning-bold',
        ghost: false,
        chips: [
          { platform: 'migu', platformName: '咪咕', title: '官方集锦' },
          { platform: 'tencent', platformName: '腾讯', title: '精华集锦' }
        ]
      },
      {
        key: 'extra',
        label: '加时视频',
        icon: 'ph:hourglass-bold',
        ghost: true,
        chips: [{ platform: 'cctv', platformName: '央视', title: '加时赛全场' }]
      },
      {
        key: 'penalty',
        label: '点球视频',
        icon: 'lucide:circle-dot',
        ghost: true,
        chips: [{ platform: 'migu', platformName: '咪咕', title: '点球大战' }]
      }
    ]
  },
  basketball: {
    league: 'NBA 总决赛',
    date: '06-14 09:00',
    home: '湖人',
    away: '勇士',
    sections: [
      {
        key: 'replay',
        label: '全场回放',
        icon: 'ph:play-circle-bold',
        ghost: false,
        chips: [
          { platform: 'tencent', platformName: '腾讯', title: '全场' },
          { platform: 'tencent', platformName: '腾讯', title: '第一节' },
          { platform: 'tencent', platformName: '腾讯', title: '第二节' },
          { platform: 'tencent', platformName: '腾讯', title: '第三节' },
          { platform: 'tencent', platformName: '腾讯', title: '第四节' }
        ]
      },
      {
        key: 'highlight',
        label: '比赛集锦',
        icon: 'ph:lightning-bold',
        ghost: false,
        chips: [
          { platform: 'migu', platformName: '咪咕', title: '官方集锦' }
        ]
      },
      // 加时一/二/三：命名与扩展 i18n（ot1/ot2/ot3）对齐；扩展侧按「OT1 有数据才显 OT2」
      // 级联显隐，加时会一直加到分出胜负——这里展示 3 个 ghost 以体现渐进式解锁效果。
      {
        key: 'ot1',
        label: '加时一',
        icon: 'ph:hourglass-bold',
        ghost: true,
        chips: [{ platform: 'tencent', platformName: '腾讯', title: '加时一全场' }]
      },
      {
        key: 'ot2',
        label: '加时二',
        icon: 'ph:hourglass-bold',
        ghost: true,
        chips: [{ platform: 'tencent', platformName: '腾讯', title: '加时二全场' }]
      },
      {
        key: 'ot3',
        label: '加时三',
        icon: 'ph:hourglass-bold',
        ghost: true,
        chips: [{ platform: 'tencent', platformName: '腾讯', title: '加时三全场' }]
      }
    ]
  }
}

// 展开状态跟随运动切换重置：常规展开、加时/点球折叠
const openKeys = ref<Record<string, boolean>>({})

watch(sport, () => {
  openKeys.value = {}
})

function isOpen(sec: SectionData): boolean {
  return openKeys.value[sec.key] ?? !sec.ghost
}

function toggle(sec: SectionData) {
  openKeys.value = { ...openKeys.value, [sec.key]: !isOpen(sec) }
}
</script>

<template>
  <div class="prog-mock">
    <!-- 运动切换 -->
    <div class="prog-mock__sports" role="tablist" aria-label="运动类型">
      <button
        v-for="s in sportTabs"
        :key="s.key"
        type="button"
        role="tab"
        class="prog-mock__sport"
        :class="{ 'prog-mock__sport--active': sport === s.key }"
        :aria-selected="sport === s.key"
        @click="sport = s.key"
      >
        <YfbIcon :name="s.icon" :size="12" />
        <span>{{ s.label }}</span>
      </button>
    </div>

    <!-- 比赛头部（无比分、无 LOGO） -->
    <div class="prog-mock__head">
      <span class="prog-mock__league">{{ config[sport].league }}</span>
      <span class="prog-mock__date">{{ config[sport].date }}</span>
    </div>
    <div class="prog-mock__teams">
      <span class="prog-mock__team-name">{{ config[sport].home }}</span>
      <span class="prog-mock__vs">VS</span>
      <span class="prog-mock__team-name">{{ config[sport].away }}</span>
    </div>

    <!-- 视频 sections -->
    <div class="prog-mock__sections">
      <div
        v-for="sec in config[sport].sections"
        :key="sec.key"
        class="prog-mock__section"
      >
        <button
          type="button"
          class="prog-mock__section-head"
          :class="{ 'prog-mock__section-head--ghost': sec.ghost }"
          :aria-expanded="isOpen(sec)"
          @click="toggle(sec)"
        >
          <span class="prog-mock__section-tag" :class="{ 'prog-mock__section-tag--ghost': sec.ghost }">
            <YfbIcon :name="sec.icon" :size="10" />
            <span>{{ sec.label }}</span>
          </span>
          <span class="prog-mock__section-arrow" aria-hidden="true">{{ isOpen(sec) ? '▼' : '▶' }}</span>
        </button>
        <div v-if="isOpen(sec)" class="prog-mock__section-body">
          <button
            v-for="(chip, idx) in sec.chips"
            :key="idx"
            type="button"
            class="prog-mock__chip yfb-press"
          >
            <span class="prog-mock__chip-platform">{{ chip.platformName }}</span>
            <span class="prog-mock__chip-sep">·</span>
            <span class="prog-mock__chip-title">{{ chip.title }}</span>
          </button>
        </div>
      </div>
    </div>

    <div class="prog-mock__footer">
      {{
        sport === 'football'
          ? '按节奏展开 · 常规 → 加时 → 点球'
          : '按节奏展开 · 常规 → 加时一 → 加时二 → 加时三（加时持续到分出胜负）'
      }}
    </div>
  </div>
</template>

<style scoped>
.prog-mock {
  border: 1px solid var(--yfb-rule);
  background-color: var(--yfb-surface);
  box-shadow: var(--yfb-shadow-2);
  border-radius: var(--yfb-radius-lg);
  overflow: hidden;
}

/* 运动切换 */
.prog-mock__sports {
  display: flex;
  gap: 1px;
  background-color: var(--yfb-rule);
  border-bottom: 1px solid var(--yfb-rule);
}

.prog-mock__sport {
  flex: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 9px;
  background-color: var(--yfb-surface-2);
  border: none;
  color: var(--yfb-text-secondary);
  font-family: inherit;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: var(--yfb-ls-button);
  cursor: pointer;
  transition: background-color var(--yfb-anim-fast) ease-out, color var(--yfb-anim-fast) ease-out;
}

.prog-mock__sport:hover {
  color: var(--yfb-ink);
}

.prog-mock__sport--active {
  background-color: var(--yfb-accent);
  color: var(--yfb-on-accent);
}

/* 头部 */
.prog-mock__head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: var(--yfb-sp-2) var(--yfb-sp-4);
  background-color: var(--yfb-surface-2);
  color: var(--yfb-ink);
}

.prog-mock__league {
  font-family: var(--yfb-font-heading);
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: var(--yfb-ls-mono);
}

.prog-mock__date {
  font-family: var(--yfb-mono);
  font-size: 11px;
  font-weight: 700;
  color: var(--yfb-accent);
}

/* 双方队伍 + vs（无 LOGO、无比分） */
.prog-mock__teams {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--yfb-sp-4);
  padding: var(--yfb-sp-6) var(--yfb-sp-4);
  background-color: var(--yfb-accent);
  color: var(--yfb-on-accent);
  border-bottom: 1px solid var(--yfb-rule);
}

.prog-mock__team-name {
  font-size: var(--yfb-fs-xl);
  font-weight: 700;
  letter-spacing: var(--yfb-ls-button);
}

.prog-mock__vs {
  font-family: var(--yfb-mono);
  font-size: 14px;
  font-weight: 700;
  letter-spacing: var(--yfb-ls-eyebrow);
  padding: var(--yfb-sp-1) var(--yfb-sp-3);
  background-color: var(--yfb-surface-2);
  color: var(--yfb-ink);
  border: 1px solid var(--yfb-rule);
  border-radius: var(--yfb-radius-sm);
}

/* Sections */
.prog-mock__sections {
  padding: var(--yfb-sp-3);
  display: flex;
  flex-direction: column;
  gap: var(--yfb-sp-2);
}

.prog-mock__section {
  border: 1px solid var(--yfb-rule);
  background-color: var(--yfb-surface);
  border-radius: var(--yfb-radius-md);
  overflow: hidden;
}

.prog-mock__section-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--yfb-sp-2);
  width: 100%;
  padding: var(--yfb-sp-2) var(--yfb-sp-3);
  background-color: transparent;
  border: none;
  cursor: pointer;
  font-family: inherit;
  color: inherit;
  text-align: left;
}

.prog-mock__section-head--ghost {
  background-color: var(--yfb-surface-2);
}

.prog-mock__section-tag {
  display: inline-flex;
  align-items: center;
  gap: calc(var(--yfb-sp-1) + var(--yfb-sp-1) / 2);
  padding: var(--yfb-sp-1) var(--yfb-sp-2);
  background-color: var(--yfb-surface-2);
  color: var(--yfb-ink);
  font-family: var(--yfb-mono);
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: var(--yfb-ls-mono);
  border-radius: var(--yfb-radius-sm);
}

.prog-mock__section-tag--ghost {
  background-color: var(--yfb-surface);
  color: var(--yfb-text-secondary);
  border: 1px dashed var(--yfb-rule-soft);
}

.prog-mock__section-arrow {
  font-family: var(--yfb-mono);
  font-size: 11px;
  font-weight: 700;
  color: var(--yfb-text-secondary);
}

.prog-mock__section-body {
  padding: var(--yfb-sp-2) var(--yfb-sp-3);
  display: flex;
  flex-wrap: wrap;
  gap: calc(var(--yfb-sp-1) + var(--yfb-sp-1) / 2);
  border-top: 1px solid var(--yfb-rule-soft);
}

.prog-mock__chip {
  display: inline-flex;
  align-items: center;
  gap: var(--yfb-sp-1);
  padding: var(--yfb-sp-1) var(--yfb-sp-2);
  background-color: var(--yfb-surface);
  color: var(--yfb-ink);
  border: 1px solid var(--yfb-rule);
  border-radius: var(--yfb-radius-sm);
  font-family: var(--yfb-mono);
  font-size: 9px;
  font-weight: 700;
  cursor: pointer;
  text-align: left;
  transition: background-color var(--yfb-anim-fast) ease-out, color var(--yfb-anim-fast) ease-out, border-color var(--yfb-anim-fast) ease-out;
}

.prog-mock__chip:hover {
  background-color: var(--yfb-surface-2);
  color: var(--yfb-accent);
  border-color: var(--yfb-accent);
}

.prog-mock__chip-platform {
  text-transform: uppercase;
  letter-spacing: var(--yfb-ls-mono);
}

.prog-mock__chip-sep {
  opacity: 0.5;
}

.prog-mock__chip-title {
  white-space: nowrap;
}

/* Footer */
.prog-mock__footer {
  display: flex;
  align-items: center;
  gap: var(--yfb-sp-2);
  padding: var(--yfb-sp-2) var(--yfb-sp-4);
  background-color: var(--yfb-surface-2);
  color: var(--yfb-accent);
  font-family: var(--yfb-mono);
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: var(--yfb-ls-mono);
  border-top: 1px solid var(--yfb-rule);
}

@media (max-width: 767px) {
  .prog-mock__teams {
    gap: var(--yfb-sp-3);
    padding: var(--yfb-sp-4) var(--yfb-sp-3);
  }

  .prog-mock__team-name {
    font-size: var(--yfb-fs-base);
  }
}

@media (prefers-reduced-motion: reduce) {
  .prog-mock__sport,
  .prog-mock__chip {
    transition: none;
  }
}
</style>
